/**
 * Bridge Error Handler - Gestion centralisée des erreurs des bridges natifs
 * Fournit des messages user-friendly pour toutes les erreurs de bridges
 */

import { toast } from 'sonner';
import { logger } from './logger';

export type BridgeErrorType =
  | 'BIOMETRIC_NOT_AVAILABLE'
  | 'BIOMETRIC_CANCELLED'
  | 'BIOMETRIC_FAILED'
  | 'BIOMETRIC_NO_SECRET'
  | 'HEALTH_PERMISSION_DENIED'
  | 'HEALTH_DATA_UNAVAILABLE'
  | 'HEALTH_BRIDGE_NOT_AVAILABLE'
  | 'HEALTH_NO_DATA'
  | 'HEALTH_WEB_ONLY'
  | 'WEBAUTHN_NOT_SUPPORTED'
  | 'WEBAUTHN_CANCELLED'
  | 'WEBAUTHN_NO_CREDENTIAL'
  | 'WEBAUTHN_REGISTRATION_FAILED'
  | 'SESSION_EXPIRED'
  | 'NETWORK_ERROR'
  | 'UNKNOWN';

interface ErrorMessage {
  title: string;
  description: string;
}

const errorMessages: Record<BridgeErrorType, ErrorMessage> = {
  BIOMETRIC_NOT_AVAILABLE: {
    title: 'Biométrie non disponible',
    description: 'Votre appareil ne supporte pas l\'authentification biométrique'
  },
  BIOMETRIC_CANCELLED: {
    title: 'Authentification annulée',
    description: 'Vous avez annulé l\'authentification biométrique'
  },
  BIOMETRIC_FAILED: {
    title: 'Échec de l\'authentification',
    description: 'L\'authentification biométrique a échoué. Réessayez ou utilisez votre mot de passe'
  },
  BIOMETRIC_NO_SECRET: {
    title: 'Session expirée',
    description: 'Veuillez vous reconnecter avec votre mot de passe'
  },
  HEALTH_PERMISSION_DENIED: {
    title: 'Permission refusée',
    description: 'Autorisez l\'accès aux données de santé dans les paramètres de votre appareil'
  },
  HEALTH_DATA_UNAVAILABLE: {
    title: 'Données indisponibles',
    description: 'Impossible de récupérer vos données de santé pour le moment'
  },
  HEALTH_BRIDGE_NOT_AVAILABLE: {
    title: 'Synchronisation indisponible',
    description: 'Utilisez l\'application mobile Koken pour synchroniser vos données de santé (pas, sommeil, exercice).'
  },
  HEALTH_NO_DATA: {
    title: 'Aucune donnée',
    description: 'Aucune donnée de santé trouvée. Vérifiez que votre app Santé enregistre vos activités.'
  },
  HEALTH_WEB_ONLY: {
    title: 'Application mobile requise',
    description: 'La synchronisation santé nécessite l\'application mobile Koken.'
  },
  WEBAUTHN_NOT_SUPPORTED: {
    title: 'Non supporté',
    description: 'Votre navigateur ne supporte pas Touch ID / Windows Hello'
  },
  WEBAUTHN_CANCELLED: {
    title: 'Authentification annulée',
    description: 'Vous avez annulé l\'authentification'
  },
  WEBAUTHN_NO_CREDENTIAL: {
    title: 'Aucun identifiant',
    description: 'Aucune méthode biométrique n\'est enregistrée pour ce compte'
  },
  WEBAUTHN_REGISTRATION_FAILED: {
    title: 'Échec de l\'enregistrement',
    description: 'Impossible d\'enregistrer la méthode biométrique. Réessayez'
  },
  SESSION_EXPIRED: {
    title: 'Session expirée',
    description: 'Votre session a expiré. Veuillez vous reconnecter'
  },
  NETWORK_ERROR: {
    title: 'Erreur réseau',
    description: 'Vérifiez votre connexion internet et réessayez'
  },
  UNKNOWN: {
    title: 'Erreur inattendue',
    description: 'Une erreur s\'est produite. Veuillez réessayer'
  }
};

export const bridgeErrorHandler = {
  /**
   * Affiche une erreur user-friendly via toast
   */
  showError: (type: BridgeErrorType, options?: { silent?: boolean }): ErrorMessage => {
    const error = errorMessages[type] || errorMessages.UNKNOWN;

    logger.warn(`[BridgeError] ${type}:`, { title: error.title, description: error.description });

    if (!options?.silent) {
      toast.error(error.title, {
        description: error.description
      });
    }

    return error;
  },

  /**
   * Parse une erreur native en BridgeErrorType
   */
  parseNativeError: (error: unknown): BridgeErrorType => {
    if (!error) return 'UNKNOWN';

    const message = String(error).toLowerCase();

    // Erreurs d'annulation utilisateur
    if (message.includes('cancel') || message.includes('user abort') || message.includes('aborted')) {
      if (message.includes('webauthn') || message.includes('credential')) {
        return 'WEBAUTHN_CANCELLED';
      }
      return 'BIOMETRIC_CANCELLED';
    }

    // Erreurs de disponibilité
    if (message.includes('not available') || message.includes('not supported')) {
      if (message.includes('webauthn') || message.includes('credential')) {
        return 'WEBAUTHN_NOT_SUPPORTED';
      }
      return 'BIOMETRIC_NOT_AVAILABLE';
    }

    // Erreurs de permission
    if (message.includes('permission') || message.includes('denied') || message.includes('not allowed')) {
      return 'HEALTH_PERMISSION_DENIED';
    }

    // Erreurs de session
    if (message.includes('session') || message.includes('expired') || message.includes('invalid token')) {
      return 'SESSION_EXPIRED';
    }

    // Erreurs réseau
    if (message.includes('network') || message.includes('offline') || message.includes('fetch')) {
      return 'NETWORK_ERROR';
    }

    // Erreurs de secret/credential manquant
    if (message.includes('no secret') || message.includes('not found')) {
      return 'BIOMETRIC_NO_SECRET';
    }

    // Erreur duplicateItem (secret déjà existant) - traiter comme un non-problème
    if (message.includes('duplicate') || message.includes('already exists')) {
      return 'BIOMETRIC_NO_SECRET'; // Will trigger re-save flow
    }

    return 'UNKNOWN';
  },

  /**
   * Retourne le message d'erreur sans l'afficher
   */
  getMessage: (type: BridgeErrorType): ErrorMessage => {
    return errorMessages[type] || errorMessages.UNKNOWN;
  }
};

export default bridgeErrorHandler;
